"use client";

import { Code, Monitor } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SeamlessWebDevelopmentManagement() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const laptopRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!laptopRef.current) return;
      const rect = laptopRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementCenter = rect.top + rect.height / 2;
      const scrollProgress = (windowHeight - elementCenter) / windowHeight;
      const tiltValue = Math.max(-8, Math.min(8, scrollProgress * 16 - 8));
      setTilt(tiltValue);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const codeSnippets = [{
    id: 1,
    label: "React Components",
    icon: Code,
    code: "export default App() { ... }"
  }, {
    id: 2,
    label: "Responsive Design",
    icon: Monitor,
    code: "@media (max-width: 768px) { ... }"
  }];
  return <section ref={ref} className="relative min-h-screen bg-dark-background text-foreground-dark py-20 px-6 flex items-center justify-center overflow-hidden">
      <div className="max-w-7xl mx-auto w-full">
        <div className="text-center mb-12">
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6 transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            Seamless Web Development & Management
          </h2>
          <p className={`text-lg md:text-xl text-foreground-dark/80 max-w-3xl mx-auto transition-all duration-[var(--duration-medium)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            Professional websites built with modern technology, managed with
            care. From design to deployment, we handle every detail.
          </p>
        </div>

        <div className="relative flex items-center justify-center">
          <div ref={laptopRef} className="relative transition-all duration-[var(--duration-slow)] hover:translate-y-[-5px]" style={{
          transform: `perspective(1000px) rotateY(${tilt}deg) translateY(${isVisible ? "0" : "2rem"})`,
          opacity: isVisible ? 1 : 0,
          transitionDuration: "var(--duration-slow)"
        }}>
            <div className="relative w-full max-w-4xl mx-auto">
              <div className="bg-muted-dark-background rounded-t-lg border-2 border-border-strong p-4 shadow-strong">
                <div className="flex gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-accent" />
                  <div className="w-3 h-3 rounded-full bg-secondary" />
                  <div className="w-3 h-3 rounded-full bg-primary" />
                </div>

                <div className="bg-dark-background rounded-md p-6 min-h-[400px] flex items-center justify-center relative overflow-hidden">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1438489/img/web_development_laptop_mockup.png" alt="Web Development Interface" className="w-full h-auto object-contain" />

                  <div className="absolute inset-0 bg-gradient-to-t from-dark-background/40 to-transparent pointer-events-none" />
                </div>
              </div>

              <div className="bg-muted-dark-background h-6 rounded-b-lg border-2 border-t-0 border-border-strong" />
            </div>
          </div>

          <div className="absolute inset-0 pointer-events-none">
            {codeSnippets.map((snippet, i) => {
            const positions = [{
              top: "15%",
              left: "8%",
              rotate: -5
            }, {
              top: "60%",
              right: "10%",
              rotate: 5
            }];
            const pos = positions[i];
            return <Card key={snippet.id} data-index={i} className={`absolute w-64 bg-muted-light-background border border-border shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              ...pos,
              transform: `rotate(${pos.rotate}deg)`,
              transitionDelay: "150ms"
            }}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-accent/10 rounded-md">
                        <snippet.icon className="w-5 h-5 text-accent" />
                      </div>
                      <span className="font-semibold text-foreground-light text-sm">
                        {snippet.label}
                      </span>
                    </div>
                    <code className="text-xs text-foreground-muted-light font-mono block bg-muted-light-background/50 p-2 rounded border border-border">
                      {snippet.code}
                    </code>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </section>;
}